import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Star } from 'lucide-react';
import { useSubmitSurvey, useSurveyResponseHistory } from '@/hooks/useCourseSurveys';
import { SurveyComparisonView } from '@/components/lms/SurveyComparisonView';

interface SurveyQuestion {
  id: string;
  question_text: string;
  question_type: 'text' | 'rating' | 'multiple_choice';
  options: string[] | null;
  sequence_order: number;
  is_required: boolean;
}

interface Survey {
  id: string;
  milestone: number;
  title: string;
  description: string | null;
  questions: SurveyQuestion[];
}

interface SurveyDialogProps {
  survey: Survey | null;
  open: boolean;
  onClose: () => void;
}

interface AnswerState {
  text?: string;
  rating?: number;
  choice?: string;
}

export function SurveyDialog({ survey, open, onClose }: SurveyDialogProps) {
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [showComparison, setShowComparison] = useState(false);
  const submitSurvey = useSubmitSurvey();
  const queryClient = useQueryClient();
  const { data: responseHistory } = useSurveyResponseHistory(survey?.id || '');

  if (!survey) return null;

  const sortedQuestions = [...survey.questions].sort((a, b) => a.sequence_order - b.sequence_order);

  const handleSubmit = () => {
    const surveyAnswers = Object.entries(answers).map(([questionId, answer]) => ({
      question_id: questionId,
      answer_text: answer.text,
      answer_rating: answer.rating,
      answer_choice: answer.choice,
    }));

    submitSurvey.mutate(
      { surveyId: survey.id, answers: surveyAnswers },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['survey-response-history', survey.id] });
          const refreshed = await queryClient.refetchQueries({ queryKey: ['survey-response-history', survey.id] });
          const history = (queryClient.getQueriesData<any[]>({ queryKey: ['survey-response-history', survey.id] })[0]?.[1]) ?? [];
          if (Array.isArray(history) && history.length >= 2) {
            setShowComparison(true);
          } else {
            onClose();
          }
        },
      }
    );
  };

  const updateAnswer = (questionId: string, field: 'text' | 'rating' | 'choice', value: string | number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value },
    }));
  };

  const isComplete = sortedQuestions.every(q => {
    if (!q.is_required) return true;
    const answer = answers[q.id];
    if (!answer) return false;
    switch (q.question_type) {
      case 'text': return !!answer.text?.trim();
      case 'rating': return answer.rating !== undefined;
      case 'multiple_choice': return !!answer.choice;
      default: return false;
    }
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !showComparison && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {showComparison ? (
          <>
            <DialogHeader>
              <DialogTitle>{survey.title} — Results</DialogTitle>
            </DialogHeader>
            <SurveyComparisonView
              questions={sortedQuestions}
              responses={responseHistory || []}
              onClose={onClose}
            />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{survey.title}</DialogTitle>
              {survey.description && (
                <DialogDescription>{survey.description}</DialogDescription>
              )}
            </DialogHeader>

            <div className="space-y-6 py-4">
              {sortedQuestions.map((question, index) => (
                <div key={question.id} className="space-y-3">
                  <Label className="text-base">
                    {index + 1}. {question.question_text}
                    {question.is_required && <span className="text-destructive ml-1">*</span>}
                  </Label>

                  {question.question_type === 'text' && (
                    <Textarea
                      value={answers[question.id]?.text || ''}
                      onChange={(e) => updateAnswer(question.id, 'text', e.target.value)}
                      placeholder="Your answer..."
                    />
                  )}

                  {question.question_type === 'rating' && (
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => updateAnswer(question.id, 'rating', rating)}
                          className="p-1 hover:scale-110 transition-transform"
                        >
                          <Star
                            className={`h-6 w-6 ${
                              (answers[question.id]?.rating || 0) >= rating
                                ? 'fill-primary text-primary'
                                : 'text-muted-foreground'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {question.question_type === 'multiple_choice' && question.options && (
                    <RadioGroup
                      value={answers[question.id]?.choice || ''}
                      onValueChange={(value) => updateAnswer(question.id, 'choice', value)}
                    >
                      {(question.options as string[]).map((option) => (
                        <div key={option} className="flex items-center space-x-2">
                          <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                          <Label htmlFor={`${question.id}-${option}`} className="font-normal">
                            {option}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Later
              </Button>
              <Button onClick={handleSubmit} disabled={!isComplete || submitSurvey.isPending}>
                {submitSurvey.isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
